





































import Vue from 'vue';
import Component from 'vue-class-component';
import { InjectReactive } from 'vue-property-decorator';

// Shared types
import { System } from '@coldtec/cos-types';

// Store types
import { ToolSystem, ToolSpace, SystemsAction, ToolAccessory, RequestAction } from '@/store/store.types';

// Step types
import { Step } from '@/steps/types';

// Internal components
import OverviewSystemAccessory from './OverviewSystemAccessory.vue';

/**
 *
 *  Component that renders the accessories section of
 *  an overview system, including a title, the accessories
 *  and an action to add an accessory.
 *
 *  (i) Also provides a public getter for getting
 *  the total price of all accessories
 *
 */
@Component({
    components: {
        OverviewSystemAccessory,
    },
})
export default class OverviewSystemAccessories extends Vue {

    @InjectReactive('toolSystem')
    private toolSystem!: ToolSystem;

    @InjectReactive('toolSpaceId')
    private toolSpaceId!: string;

    @InjectReactive('toolSpaces')
    private toolSpaces!: Array<ToolSpace>;

    @InjectReactive('system')
    private system!: System;

    /**
     *  Used to get all the (tool) accessories of the
     *  space for which the system is rendered.
     */
    private get toolAccessories(): Array<ToolAccessory> {

        const toolAccessories: Array<ToolAccessory> = [];

        /**
         *  Add the (original) data of every accessory that is selected for the space/system.
         */
        for (const toolAccessory of this.toolSystem.accessories) {

            if (!toolAccessory.selected) {

                continue;

            }

            toolAccessories.push(toolAccessory);

        }

        return toolAccessories;

    }

    /**
     *  Used to get the total price of all accessories.
     */
    public get accessoriesPrice(): number {

        let price = 0;

        for (const accesory of this.toolAccessories) {

            price += accesory.data.price || 0;

        }

        return price;

    }

    /**
     *
     *  Used to remove an accessory from the system. It removes it both
     *  from the tool spaces state and the request spaces state.
     *
     *  @param accessory The (tool) accessory to remove
     *
     */
    private remove(accesory: ToolAccessory): void {

        /**
         *
         *  Removes the accessory from the overview accessories
         *
         *  (i) Because of the `toolAccessories` getter
         *
         */
        accesory.selected = false;

        /**
         *  Remove the accessory from the (tool) spaces
         *  for which it was selected (/added).
         */
        for (const space of this.toolSpaces) {

            if (!Array.isArray(space.data.accessories)) {

                continue;

            }

            const index: number = space.data.accessories.indexOf(accesory.guid);

            space.data.accessories.splice(index, 1);

        }

        /**
         *
         *  Sync the request spaces, also removing it from the request space.
         *
         *  (!) Important, because if it's not removed from the request
         *  space, it will automatically be 'added' to the suggestion
         *  state again when the systems step is loaded.
         *
         *  (i) See the `SystemsAction.SYNC_SUGGESTIONS` action for more
         *
         */
        this.$store.dispatch(RequestAction.SYNC_SPACES);

    }

    /**
     *  Used to open the system's step to allow the user to add an accessory.
     */
    private async add(): Promise<void> {

        await this.$store.dispatch(SystemsAction.SHOW_FOR_SPACE, this.toolSpaceId);

        void this.$router.push(Step.Systems);

    }

}
